import { t } from '@lingui/macro';
import React, { useState, useCallback, useRef, useEffect } from 'react';

import { MAX_NAME_LENGTH } from '@components/bulk_management/people_management/PersonCard';
import { IdentitySearchReturnFormat } from '@components/common/hooks/useIdentitySearch';
import { useIdentitySearchOnDemand } from '@components/common/hooks/useIdentitySearchOnDemand';
import  Tooltip  from '@components/common/tooltip/main';
import { Person } from '@components/people_tags/customPeopleTagTypes';

import { PeopleTagScopeConfirmModal } from './PeopleTagScopeConfirmModal';
import {
  PersonTag,
  PersonImage,
  PersonName,
  PersonNameText,
  RemovePersonButton,
  CustomDropdown,
  DropdownHeader,
  DropdownIcon,
  InputStyled,
  FaceThumbnailImage,
  SuggestionsDropdownWrapper,
  SuggestionsDropdown,
  SuggestionItemButton,
  SuggestionItemImage,
  SuggestionItemName,
  SuggestionItemCount,
  AddNameButton
} from './styles/PeopleInAssetsComponents';

// Face data structure used internally
export interface FaceData {
  identityId?: string;
  faceId?: string;
  name: string;
  assetUrl: string;
  faceThumbnailUrl: string;
  isHidden: boolean;
  faceCount: number;
}

interface PeopleTagEditFieldProps {
  face: FaceData;
  onSplitIdentity: (identityId: string, faceId: string, name?: string, targetIdentityId?: string) => void;
  onMergeIdentities: (sourceIdentityId: string, destinationIdentity: Person, faceId: string) => void;
  onUpdateIdentityName: (identityId: string, name: string, faceId: string) => void;
  /** Fired on first non-empty input while naming an unnamed face (#13 timing). */
  onUnnamedNameTypingStart?: (faceId: string) => void;
  assetCount: number;
}

export const PeopleTagEditField: React.FC<PeopleTagEditFieldProps> = ({
  face,
  onSplitIdentity,
  onMergeIdentities,
  onUpdateIdentityName,
  onUnnamedNameTypingStart,
  assetCount
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [showScopeConfirmModal, setShowScopeConfirmModal] = useState(false);
  const [allAssetsScopeAction, setAllAssetsScopeAction] = useState<() => void>(() => () => {});
  const [thisAssetScopeAction, setThisAssetScopeAction] = useState<() => void>(() => () => {});
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const hasName = face.name && face.name.trim() !== '';
  const dropdownRef = useRef<HTMLDivElement>(null);
  const nameTextRef = useRef<HTMLParagraphElement>(null);
  const unnamedInputPrevRef = useRef<string>('');
  const isCommittingRef = useRef(false);

  // Close dropdown and clear input when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        if (isOpen || searchInput) {
          setIsOpen(false);
          setSearchInput('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, searchInput]);

  // Check if text is truncated
  useEffect(() => {
    const checkTruncation = (): void => {
      if (nameTextRef.current && hasName && face.name) {
        const element = nameTextRef.current;
        // Check if text is actually truncated by comparing scroll width to client width
        setIsTextTruncated(element.scrollWidth > element.clientWidth);
      } else {
        setIsTextTruncated(false);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(checkTruncation);
    }, 0);

    // Recheck on window resize to handle responsive layout changes
    window.addEventListener('resize', checkTruncation);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [hasName, face.name]);

  useEffect(() => {
    unnamedInputPrevRef.current = '';
  }, [face.faceId]);

  const { results: suggestions } = useIdentitySearchOnDemand<Person>({
    searchTerm: searchInput,
    isDropdownOpen: isOpen,
    limit: 50,
    excludeIdentityId: face.identityId,
    returnFormat: IdentitySearchReturnFormat.PERSON
  });

  const handleSearchInputChange = useCallback(
    (value: string): void => {
      if (
        !hasName &&
        face.faceId &&
        unnamedInputPrevRef.current.trim() === '' &&
        value.trim() !== ''
      ) {
        onUnnamedNameTypingStart?.(face.faceId);
      }
      unnamedInputPrevRef.current = value;
      setSearchInput(value);
      setIsOpen(true);
    },
    [face.faceId, hasName, onUnnamedNameTypingStart]
  );

  const commitName = useCallback((): void => {
    if (!searchInput.trim()) return;
    isCommittingRef.current = true;
    setIsOpen(false);
    setAllAssetsScopeAction(() => () => onUpdateIdentityName(face.identityId, searchInput, face.faceId));
    setThisAssetScopeAction(() => () => onSplitIdentity(face.identityId, face.faceId, searchInput, null));
    setShowScopeConfirmModal(true);
  }, [onUpdateIdentityName, onSplitIdentity, face.identityId, face.faceId, searchInput]);

  const handleKeyUp = useCallback((e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      commitName();
    }

    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchInput('');
      (e.target as HTMLInputElement).blur();
    }
  }, [commitName]);

  const handleInputBlur = useCallback((): void => {
    if (isCommittingRef.current) return;
    setSearchInput('');
    setIsOpen(false);
  }, []);

  const preventBlur = useCallback((e: React.MouseEvent): void => {
    e.preventDefault();
  }, []);

  const onIdentitySelected = useCallback((selectedIdentity: Person): void => {
    isCommittingRef.current = true;
    setIsOpen(false);
    setAllAssetsScopeAction(() => () => onMergeIdentities(face.identityId, selectedIdentity, face.faceId));
    setThisAssetScopeAction(
      () => () => onSplitIdentity(face.identityId, face.faceId, selectedIdentity.tag_name, selectedIdentity.person_id)
    );
    setShowScopeConfirmModal(true);
  }, [face.faceId, face.identityId, onMergeIdentities, onSplitIdentity]);

  const onRemoveFaceName = (): void => {
    setIsOpen(false);
    setSearchInput('');
    setAllAssetsScopeAction(() => () => onUpdateIdentityName(face.identityId, '', face.faceId));
    setThisAssetScopeAction(() => () => onSplitIdentity(face.identityId, face.faceId, '', null));
    setShowScopeConfirmModal(true);
  };

  const toggleDropdown = useCallback((): void => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleModalClose = (): void => {
    isCommittingRef.current = false;
    setShowScopeConfirmModal(false);
    setSearchInput('');
  };

  const handlePrimaryAction = (): void => {
    isCommittingRef.current = false;
    allAssetsScopeAction();
    setShowScopeConfirmModal(false);
    setSearchInput('');
  };

  const handleSecondaryAction = (): void => {
    isCommittingRef.current = false;
    thisAssetScopeAction();
    setShowScopeConfirmModal(false);
    setSearchInput('');
  };

  return (
    <>
      <PersonTag>
        <PersonImage>
          <FaceThumbnailImage
            alt={hasName ? face.name : t`Unidentified person`}
            src={face.faceThumbnailUrl}
          />
        </PersonImage>
        <PersonName>
          {hasName ? (
            <>
              {isTextTruncated ? (
                <Tooltip
                  placement="top"
                  tooltipContent={face.name}
                  tooltipId={`person-name-tooltip-${face.faceId || face.identityId || 'unknown'}`}
                  type="dark"
                >
                  <PersonNameText ref={nameTextRef}>{face.name}</PersonNameText>
                </Tooltip>
              ) : (
                <PersonNameText ref={nameTextRef}>{face.name}</PersonNameText>
              )}
            <RemovePersonButton
              aria-label={t`Remove ${face.name}`}
              onClick={(): void => onRemoveFaceName()}
            >
              <span className="icon bff-close" />
            </RemovePersonButton>
            </>
          ) : (
            <CustomDropdown ref={dropdownRef}>
              <DropdownHeader>
                <InputStyled
                  maxLength={MAX_NAME_LENGTH}
                  onBlur={handleInputBlur}
                  onChange={(e): void => handleSearchInputChange(e.target.value)}
                  onKeyUp={(e): void => handleKeyUp(e)}
                  placeholder={t`Name person`}
                  type="text"
                  value={searchInput}
                />
                <DropdownIcon onClick={toggleDropdown} onMouseDown={preventBlur}>
                  <span className={`icon ${isOpen ? 'bff-caret-up' : 'bff-caret-down'}`} />
                </DropdownIcon>
              </DropdownHeader>

              {isOpen && (suggestions.length > 0 || searchInput.trim()) && (
                <SuggestionsDropdownWrapper>
                  {suggestions.length > 0 && (
                    <SuggestionsDropdown>
                      {suggestions.map((person) => (
                        <li key={person.person_id}>
                          <SuggestionItemButton
                            onClick={(): void => onIdentitySelected(person)}
                            onMouseDown={preventBlur}
                            type="button"
                          >
                            <SuggestionItemImage alt={t`person thumbnail`} src={person.face_uri} />
                            <SuggestionItemName>{person.tag_name}</SuggestionItemName>
                            {person.face_count ? (
                              <SuggestionItemCount>{person.face_count}</SuggestionItemCount>
                            ) : null}
                          </SuggestionItemButton>
                        </li>
                      ))}
                    </SuggestionsDropdown>
                  )}
                  {searchInput.trim() && (
                    <AddNameButton
                      onClick={commitName}
                      onMouseDown={preventBlur}
                      type="button"
                    >
                      <span className="add-label">{t`Add:`}</span>
                      <span className="add-name">{searchInput.trim()}</span>
                    </AddNameButton>
                  )}
                </SuggestionsDropdownWrapper>
              )}
            </CustomDropdown>
          )}
        </PersonName>
      </PersonTag>
      <PeopleTagScopeConfirmModal
        assetCount={assetCount}
        isOpen={showScopeConfirmModal}
        onClose={handleModalClose}
        onPrimaryAction={handlePrimaryAction}
        onSecondaryAction={handleSecondaryAction}
      />
    </>
  );
};
